import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Globe, Copy, ExternalLink, Save, Image as ImageIcon, Clock, UtensilsCrossed, BarChart3, LinkIcon, Plus, Trash2, Trash, Files, ArrowUp, ArrowDown } from "lucide-react";

// Sections available for each page. Toggled via checkboxes in the Pages tab.
const AVAILABLE_SECTIONS = [
  { key: "hero",     fr: "Bannière" },
  { key: "about",    fr: "À propos" },
  { key: "services", fr: "Services" },
  { key: "why_us",   fr: "Pourquoi nous" },
  { key: "video",    fr: "Vidéo" },
  { key: "gallery",  fr: "Galerie" },
  { key: "hours",    fr: "Horaires" },
  { key: "menu",     fr: "Menu / Prix" },
  { key: "location", fr: "Localisation" },
  { key: "contact",  fr: "Contact" },
];

// One-click template producing 4 real pages with clean URLs.
const STANDARD_PAGES = [
  { slug: "home",     title: "Accueil",  sections: ["hero", "about", "services", "video", "contact"] },
  { slug: "about",    title: "À propos", sections: ["hero", "about", "why_us"] },
  { slug: "services", title: "Services", sections: ["hero", "services", "menu"] },
  { slug: "contact",  title: "Contact",  sections: ["hero", "hours", "location", "contact"] },
];

const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "page";

const DAYS = [
  { key: "mon", fr: "Lundi", en: "Monday" },
  { key: "tue", fr: "Mardi", en: "Tuesday" },
  { key: "wed", fr: "Mercredi", en: "Wednesday" },
  { key: "thu", fr: "Jeudi", en: "Thursday" },
  { key: "fri", fr: "Vendredi", en: "Friday" },
  { key: "sat", fr: "Samedi", en: "Saturday" },
  { key: "sun", fr: "Dimanche", en: "Sunday" },
];

export default function WebsiteStudio() {
  const { cid } = useParams();
  const { lang } = useAuth();
  const [company, setCompany] = useState(null);
  const [brief, setBrief] = useState("");
  const [tone, setTone] = useState("professional");
  const [content, setContent] = useState(null);
  const [publishedSlug, setPublishedSlug] = useState(null);
  const [customDomain, setCustomDomain] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("text");  // text | media | hours | menu | pages | domain | stats
  const [theme, setTheme] = useState(content?.theme || "light");  // light | dark — sent to AI generator

  const publicUrl = publishedSlug ? `${window.location.origin}/site/${publishedSlug}` : null;

  useEffect(() => {
    http.get(`/companies/${cid}`).then((r) => setCompany(r.data)).catch(() => {});
    http.get(`/websites/mine/${cid}`).then((r) => {
      setContent(r.data.content);
      setPublishedSlug(r.data.slug);
      setCustomDomain(r.data.custom_domain || "");
    }).catch(() => {});
    http.get(`/websites/analytics/${cid}`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
  }, [cid]);

  const generate = async () => {
    setBusy(true);
    try {
      // Feed the AI every asset the SME already provided so the very first
      // draft already integrates their photos, video and logo — no manual
      // wiring needed afterwards.
      const payload = {
        company_id: cid,
        brief,
        tone,
        theme,
        logo_url: content?.logo_url || null,
        photo_urls: content?.gallery || [],
        video_url: content?.video_url || null,
      };
      const { data } = await http.post("/websites/generate", payload);
      // Preserve editor-side additions (hours, menu, custom domain) across
      // regens — the AI response overrides only the copy + pages structure.
      setContent({ ...(content || {}), ...data });
      toast.success(lang === "fr" ? "Site généré avec vos photos, vidéo et logo — modifiez si besoin" : "Site generated with your photos, video and logo — edit if needed");
    } catch (e) { toast.error(e.response?.data?.detail || "AI error"); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!content) return;
    setBusy(true);
    try {
      const { data } = await http.post("/websites/publish", { company_id: cid, content });
      setPublishedSlug(data.slug);
      toast.success(lang === "fr" ? `Publié ! → /site/${data.slug}` : `Published! → /site/${data.slug}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const saveDomain = async () => {
    setBusy(true);
    try {
      const { data } = await http.put("/websites/domain", { company_id: cid, custom_domain: customDomain || null });
      setCustomDomain(data.custom_domain || "");
      toast.success(lang === "fr"
        ? "Domaine enregistré — configurez maintenant votre DNS"
        : "Domain saved — now configure your DNS");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const setField = (k, v) => setContent({ ...(content || {}), [k]: v });
  const updateGallery = (urls) => setField("gallery", urls);
  const updateHours = (dayKey, val) => setField("hours", { ...(content?.hours || {}), [dayKey]: val });
  const updateMenu = (items) => setField("menu", items);

  const gallery = content?.gallery || [];
  const hours = content?.hours || {};
  const menu = content?.menu || [];
  const pages = content?.pages || [];
  const setPages = (arr) => setContent({ ...(content || {}), pages: arr });

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      data-testid={`studio-tab-${id}`}
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${tab === id ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">
              {lang === "fr" ? "Website Studio IA" : "AI Website Studio"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {lang === "fr" ? `Générez un vrai site web 1-page pour ${company?.name || "votre entreprise"} — hébergé sur Proxijob.` : `Generate a real 1-page website for ${company?.name || "your business"} — hosted on Proxijob.`}
            </p>
          </div>
          {publicUrl && (
            <div className="flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs">
              <Globe className="w-3 h-3 text-emerald-700" />
              <span className="font-mono text-emerald-800 truncate max-w-[240px]">{publicUrl}</span>
              <button data-testid="copy-site-url" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copié !"); }} className="text-emerald-700 hover:text-emerald-900"><Copy className="w-3 h-3" /></button>
              <a href={publicUrl} target="_blank" rel="noreferrer" className="text-emerald-700 hover:text-emerald-900"><ExternalLink className="w-3 h-3" /></a>
            </div>
          )}
        </div>

        {/* Brief + Generate */}
        <div className="rounded-3xl bg-white border border-slate-200 p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
            {lang === "fr" ? "Étape 1 — Racontez à l'IA votre entreprise" : "Step 1 — Tell the AI about your business"}
          </p>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            {lang === "fr"
              ? "L'IA se charge de tout : titres, textes, structure, ton, 4 pages. Vous pouvez ajouter votre logo, photos et vidéo — l'IA les intègrera automatiquement."
              : "The AI does everything: titles, copy, structure, tone, 4 pages. Add your logo, photos and video — the AI will weave them in automatically."}
          </p>

          {/* One-shot media assets — feeds directly into the AI generator */}
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#F97316] text-sm">
              <ImageIcon className="w-4 h-4 text-[#F97316]" />
              <span className="flex-1 truncate">
                {content?.logo_url ? (lang === "fr" ? "✓ Logo chargé" : "✓ Logo uploaded") : (lang === "fr" ? "Ajouter votre logo" : "Add your logo")}
              </span>
              <input
                data-testid="upload-logo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 6 * 1024 * 1024) { toast.error(lang === "fr" ? "Logo > 6 Mo" : "Logo > 6 MB"); return; }
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    const { data } = await http.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setContent({ ...(content || {}), logo_url: data.url });
                    toast.success(lang === "fr" ? "Logo prêt" : "Logo ready");
                  } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                  e.target.value = "";
                }}
              />
            </label>
            <button
              data-testid="ai-generate-logo"
              onClick={async () => {
                setBusy(true);
                try {
                  const { data } = await http.post("/websites/logo/generate", { company_id: cid, style_hint: tone });
                  setContent({ ...(content || {}), logo_url: data.url });
                  toast.success(lang === "fr" ? "Logo IA généré ✨" : "AI logo generated ✨");
                } catch (err) { toast.error(err.response?.data?.detail || "AI logo failed"); }
                setBusy(false);
              }}
              disabled={busy}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-[#F97316]/40 hover:border-[#F97316] text-sm bg-orange-50/50 disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4 text-[#F97316]" />
              <span className="flex-1 truncate text-left">
                {lang === "fr" ? "Générer un logo IA" : "Generate AI logo"}
              </span>
            </button>
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#F97316] text-sm">
              <ImageIcon className="w-4 h-4 text-[#F97316]" />
              <span className="flex-1 truncate">
                {gallery.length > 0 ? (lang === "fr" ? `✓ ${gallery.length} photos` : `✓ ${gallery.length} photos`) : (lang === "fr" ? "Ajouter des photos" : "Add photos")}
              </span>
              <input
                data-testid="upload-quick-photos"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []); if (!files.length) return;
                  setBusy(true);
                  const uploaded = [];
                  for (const f of files) {
                    if (f.size > 6 * 1024 * 1024) continue;
                    try {
                      const fd = new FormData(); fd.append("file", f);
                      const { data } = await http.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      uploaded.push(data.url);
                    } catch {}
                  }
                  if (uploaded.length) updateGallery([...(gallery || []), ...uploaded]);
                  setBusy(false); e.target.value = "";
                }}
              />
            </label>
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#F97316] text-sm">
              <ImageIcon className="w-4 h-4 text-[#F97316]" />
              <span className="flex-1 truncate">
                {content?.video_url ? (lang === "fr" ? "✓ Vidéo prête" : "✓ Video ready") : (lang === "fr" ? "Ajouter vidéo (MP4/YT)" : "Add video (MP4/YT)")}
              </span>
              <input
                data-testid="upload-quick-video"
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 25 * 1024 * 1024) { toast.error("> 25 MB"); return; }
                  setBusy(true);
                  try {
                    const fd = new FormData(); fd.append("file", f);
                    const { data } = await http.post("/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setField("video_url", data.url);
                    toast.success(lang === "fr" ? "Vidéo prête" : "Video ready");
                  } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                  setBusy(false); e.target.value = "";
                }}
              />
            </label>
          </div>

          <div className="grid md:grid-cols-[1fr_auto] gap-3">
            <Textarea
              data-testid="site-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder={lang === "fr" ? "Ex : Boulangerie artisanale à Terrebonne, pains au levain, viennoiseries pur beurre, ouverte depuis 2018. Ambiance chaleureuse." : "E.g. Artisan bakery in Terrebonne, sourdough breads, pure butter viennoiseries, open since 2018. Warm ambiance."}
              rows={3}
            />
            <div className="flex md:flex-col gap-2">
              <Select value={theme} onValueChange={(v) => { setTheme(v); if (content) setField("theme", v); }}>
                <SelectTrigger data-testid="site-theme" className="w-full md:w-40"><SelectValue placeholder={lang === "fr" ? "Thème" : "Theme"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">☀️ {lang === "fr" ? "Clair et attirant" : "Bright & clean"}</SelectItem>
                  <SelectItem value="dark">🌙 {lang === "fr" ? "Sombre et chic" : "Dark & elegant"}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="site-tone" className="w-full md:w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">{lang === "fr" ? "Professionnel" : "Professional"}</SelectItem>
                  <SelectItem value="friendly">{lang === "fr" ? "Chaleureux" : "Friendly"}</SelectItem>
                  <SelectItem value="luxury">{lang === "fr" ? "Luxe" : "Luxury"}</SelectItem>
                  <SelectItem value="fun">{lang === "fr" ? "Fun" : "Fun"}</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="site-generate-btn" onClick={generate} disabled={busy} className="bg-[#F97316] hover:bg-[#EA580C] rounded-full h-10">
                <Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "…" : (lang === "fr" ? "Générer avec IA" : "Generate with AI")}
              </Button>
            </div>
          </div>
        </div>

        {content && (
          <>
            {/* Tabs */}
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <TabBtn id="text"   icon={Sparkles}         label={lang === "fr" ? "Texte" : "Text"} />
              <TabBtn id="media"  icon={ImageIcon}        label={lang === "fr" ? "Photos" : "Photos"} />
              <TabBtn id="hours"  icon={Clock}            label={lang === "fr" ? "Horaires" : "Hours"} />
              <TabBtn id="menu"   icon={UtensilsCrossed}  label={lang === "fr" ? "Menu / Prix" : "Menu / Prices"} />
              <TabBtn id="pages"  icon={Files}            label={lang === "fr" ? "Pages" : "Pages"} />
              <TabBtn id="domain" icon={LinkIcon}         label={lang === "fr" ? "Domaine" : "Domain"} />
              <TabBtn id="stats"  icon={BarChart3}        label={lang === "fr" ? "Analytique" : "Analytics"} />
            </div>

            <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-6">
              {tab === "text" && (
                <div className="grid gap-4" data-testid="tab-text">
                  <div><Label>Tagline</Label><Input data-testid="site-tagline" value={content.tagline || ""} onChange={(e) => setField("tagline", e.target.value)} /></div>
                  <div><Label>Hero title</Label><Input data-testid="site-hero-title" value={content.hero_title || ""} onChange={(e) => setField("hero_title", e.target.value)} /></div>
                  <div><Label>Hero subtitle</Label><Textarea data-testid="site-hero-sub" rows={2} value={content.hero_sub || ""} onChange={(e) => setField("hero_sub", e.target.value)} /></div>
                  <div><Label>{lang === "fr" ? "Bouton CTA" : "CTA button"}</Label><Input value={content.cta_text || ""} onChange={(e) => setField("cta_text", e.target.value)} /></div>
                  <div><Label>{lang === "fr" ? "Titre À propos" : "About title"}</Label><Input value={content.about_title || ""} onChange={(e) => setField("about_title", e.target.value)} /></div>
                  <div><Label>{lang === "fr" ? "Texte À propos" : "About body"}</Label><Textarea rows={4} value={content.about_body || ""} onChange={(e) => setField("about_body", e.target.value)} /></div>
                  <div><Label>{lang === "fr" ? "Introduction Contact" : "Contact intro"}</Label><Input value={content.contact_intro || ""} onChange={(e) => setField("contact_intro", e.target.value)} /></div>
                  <div><Label>Footer tagline</Label><Input value={content.footer_tagline || ""} onChange={(e) => setField("footer_tagline", e.target.value)} /></div>
                  <div><Label>{lang === "fr" ? "Couleur d'accent" : "Accent color"}</Label>
                    <div className="flex items-center gap-2 mt-1"><input data-testid="site-color" type="color" value={content.accent_color || "#F97316"} onChange={(e) => setField("accent_color", e.target.value)} className="h-10 w-16 rounded-lg border cursor-pointer" /><span className="text-xs text-slate-500 font-mono">{content.accent_color || "#F97316"}</span></div>
                  </div>
                  <div><Label>{lang === "fr" ? "Ambiance du site" : "Site vibe"}</Label>
                    <div className="mt-1 flex gap-2">
                      <button
                        data-testid="theme-light-btn"
                        onClick={() => { setTheme("light"); setField("theme", "light"); }}
                        className={`flex-1 px-3 py-2 rounded-xl border text-sm text-left transition ${(content.theme || "light") === "light" ? "border-[#F97316] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        ☀️ <span className="font-medium">{lang === "fr" ? "Clair et attirant" : "Bright & clean"}</span>
                        <p className="text-[10px] text-slate-500 mt-0.5">{lang === "fr" ? "Fond blanc, moderne, aéré" : "White, modern, airy"}</p>
                      </button>
                      <button
                        data-testid="theme-dark-btn"
                        onClick={() => { setTheme("dark"); setField("theme", "dark"); }}
                        className={`flex-1 px-3 py-2 rounded-xl border text-sm text-left transition ${content.theme === "dark" ? "border-[#F97316] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        🌙 <span className="font-medium">{lang === "fr" ? "Sombre et chic" : "Dark & elegant"}</span>
                        <p className="text-[10px] text-slate-500 mt-0.5">{lang === "fr" ? "Fond nuit, luxe, mystère" : "Night, luxury, mystery"}</p>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {tab === "media" && (
                <div data-testid="tab-media">
                  <Label>{lang === "fr" ? "Ajouter des photos" : "Add photos"}</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-3 leading-relaxed">
                    {lang === "fr"
                      ? "Choisissez des photos directement depuis votre téléphone ou ordinateur, OU collez des URLs (une par ligne)."
                      : "Pick photos directly from your phone or computer, OR paste URLs (one per line)."}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-medium">
                      <ImageIcon className="w-4 h-4" />
                      {lang === "fr" ? "Choisir des photos" : "Choose photos"}
                      <input
                        data-testid="upload-photos-input"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (!files.length) return;
                          setBusy(true);
                          const uploaded = [];
                          for (const file of files) {
                            if (file.size > 6 * 1024 * 1024) {
                              toast.error(lang === "fr" ? `${file.name} > 6 Mo — trop grande` : `${file.name} > 6 MB — too large`);
                              continue;
                            }
                            try {
                              const fd = new FormData();
                              fd.append("file", file);
                              const { data } = await http.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                              uploaded.push(data.url);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || `${file.name} — upload failed`);
                            }
                          }
                          if (uploaded.length) {
                            updateGallery([...(gallery || []), ...uploaded]);
                            toast.success(lang === "fr" ? `${uploaded.length} photo(s) ajoutée(s)` : `${uploaded.length} photo(s) added`);
                          }
                          setBusy(false);
                          e.target.value = "";
                        }}
                        className="hidden"
                      />
                    </label>
                    <span className="text-xs text-slate-500">{lang === "fr" ? "Max 6 Mo par photo" : "Max 6 MB per photo"}</span>
                  </div>
                  <Label className="text-xs text-slate-500">{lang === "fr" ? "OU coller des URLs (une par ligne)" : "OR paste URLs (one per line)"}</Label>
                  <Textarea
                    data-testid="site-gallery"
                    value={gallery.join("\n")}
                    onChange={(e) => updateGallery(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                    rows={4}
                    placeholder="https://images.unsplash.com/…"
                    className="mt-1.5 font-mono text-xs"
                  />
                  {gallery.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 md:grid-cols-4 gap-2">
                      {gallery.map((src, i) => (
                        <div key={i} className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                          <img src={src} alt="" className="w-full h-full object-cover" />
                          <button
                            data-testid={`del-photo-${i}`}
                            onClick={() => updateGallery(gallery.filter((_, j) => j !== i))}
                            className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Video block */}
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <Label>{lang === "fr" ? "Vidéo — YouTube, Vimeo ou fichier MP4" : "Video — YouTube, Vimeo or MP4 file"}</Label>
                    <p className="text-xs text-slate-500 mt-1 mb-3 leading-relaxed">
                      {lang === "fr"
                        ? "Collez un lien YouTube/Vimeo OU téléversez directement un fichier MP4 depuis votre téléphone (max 25 Mo)."
                        : "Paste a YouTube/Vimeo link OR upload an MP4 file straight from your phone (max 25 MB)."}
                    </p>
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] hover:bg-[#172554] text-white text-sm font-medium">
                        <ImageIcon className="w-4 h-4" />
                        {lang === "fr" ? "Téléverser MP4" : "Upload MP4"}
                        <input
                          data-testid="upload-video-input"
                          type="file"
                          accept="video/mp4,video/quicktime,video/webm"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 25 * 1024 * 1024) {
                              toast.error(lang === "fr" ? "Fichier > 25 Mo — trop volumineux" : "File > 25 MB — too large");
                              e.target.value = "";
                              return;
                            }
                            setBusy(true);
                            try {
                              const fd = new FormData();
                              fd.append("file", file);
                              const { data } = await http.post("/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                              setField("video_url", data.url);
                              toast.success(lang === "fr" ? "Vidéo téléversée" : "Video uploaded");
                            } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                            setBusy(false);
                            e.target.value = "";
                          }}
                          className="hidden"
                        />
                      </label>
                      {content.video_url?.startsWith("/api/videos/") && (
                        <span className="text-xs text-emerald-700 font-medium">✓ {lang === "fr" ? "Fichier téléversé" : "File uploaded"}</span>
                      )}
                    </div>
                    <Input
                      data-testid="site-video-url"
                      value={content.video_url || ""}
                      onChange={(e) => setField("video_url", e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="max-w-xl font-mono text-xs"
                    />
                  </div>
                </div>
              )}

              {tab === "hours" && (
                <div className="grid gap-3" data-testid="tab-hours">
                  <p className="text-xs text-slate-500">{lang === "fr" ? "Format libre : '9h-18h', 'Fermé', '10:00-14:00 · 17:00-21:00'…" : "Free format: '9-6', 'Closed', '10:00-14:00 · 17:00-21:00'…"}</p>
                  {DAYS.map((d) => (
                    <div key={d.key} className="grid grid-cols-[110px_1fr] items-center gap-3">
                      <Label>{lang === "fr" ? d.fr : d.en}</Label>
                      <Input data-testid={`site-hours-${d.key}`} value={hours[d.key] || ""} onChange={(e) => updateHours(d.key, e.target.value)} placeholder={lang === "fr" ? "9h - 18h" : "9am - 6pm"} />
                    </div>
                  ))}
                </div>
              )}

              {tab === "menu" && (
                <div className="grid gap-3" data-testid="tab-menu">
                  <p className="text-xs text-slate-500">{lang === "fr" ? "Ajoutez vos produits, services ou plats avec leur prix." : "Add products, services or dishes with their price."}</p>
                  {menu.map((item, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_100px_auto] gap-2 items-center">
                      <Input placeholder={lang === "fr" ? "Nom" : "Name"} value={item.name || ""} onChange={(e) => updateMenu(menu.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                      <Input placeholder="Description" value={item.desc || ""} onChange={(e) => updateMenu(menu.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))} />
                      <Input placeholder={lang === "fr" ? "Prix" : "Price"} value={item.price || ""} onChange={(e) => updateMenu(menu.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
                      <Button size="sm" variant="ghost" onClick={() => updateMenu(menu.filter((_, j) => j !== i))} className="rounded-full h-9"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  <Button
                    data-testid="menu-add-btn"
                    variant="outline"
                    onClick={() => updateMenu([...menu, { name: "", desc: "", price: "" }])}
                    className="rounded-full w-fit"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> {lang === "fr" ? "Ajouter un produit" : "Add item"}
                  </Button>
                </div>
              )}

              {tab === "pages" && (
                <div data-testid="tab-pages" className="grid gap-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <Label className="text-base">{lang === "fr" ? "Pages du site" : "Site pages"}</Label>
                      <p className="text-xs text-slate-500 mt-1 max-w-lg leading-relaxed">
                        {lang === "fr"
                          ? "Passez de « site 1 page » à un vrai site avec Accueil, À propos, Services et Contact — chacun avec une URL propre et un menu sticky."
                          : "Turn your 1-page site into a real multi-page website — each page gets a clean URL and a sticky menu."}
                      </p>
                    </div>
                    {pages.length === 0 && (
                      <Button
                        data-testid="pages-create-standard"
                        onClick={() => setPages(STANDARD_PAGES)}
                        className="bg-[#F97316] hover:bg-[#EA580C] rounded-full"
                      >
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        {lang === "fr" ? "Créer les pages standards" : "Create standard pages"}
                      </Button>
                    )}
                  </div>

                  {pages.length === 0 ? (
                    <div className="rounded-xl bg-slate-50 border border-dashed border-slate-300 p-6 text-center">
                      <Files className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">
                        {lang === "fr"
                          ? "Ce site est actuellement une seule longue page. Cliquez ci-dessus pour créer les 4 pages standards."
                          : "This site is currently one long page. Click above to create the 4 standard pages."}
                      </p>
                    </div>
                  ) : (
                    <>
                      {pages.map((p, i) => (
                        <div key={i} data-testid={`page-row-${i}`} className="rounded-xl border border-slate-200 p-4">
                          <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-center">
                            <div>
                              <Label className="text-xs text-slate-500">{lang === "fr" ? "Titre affiché" : "Menu title"}</Label>
                              <Input
                                data-testid={`page-title-${i}`}
                                value={p.title || ""}
                                onChange={(e) => {
                                  const t = e.target.value;
                                  setPages(pages.map((x, j) => j === i ? { ...x, title: t, slug: x.slug || slugify(t) } : x));
                                }}
                                placeholder="Accueil"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">{lang === "fr" ? "URL (slug)" : "URL slug"}</Label>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400 font-mono shrink-0">/site/{publishedSlug || "…"}/</span>
                                <Input
                                  data-testid={`page-slug-${i}`}
                                  value={p.slug || ""}
                                  onChange={(e) => setPages(pages.map((x, j) => j === i ? { ...x, slug: slugify(e.target.value) } : x))}
                                  className="font-mono text-xs"
                                  disabled={i === 0}
                                />
                              </div>
                              {i === 0 && <p className="text-[10px] text-slate-400 mt-0.5">{lang === "fr" ? "Page d'accueil — URL fixe" : "Home page — fixed URL"}</p>}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => { const a = [...pages]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setPages(a); }} className="rounded-full h-8 w-8"><ArrowUp className="w-3.5 h-3.5" /></Button>
                              <Button size="icon" variant="ghost" disabled={i === pages.length - 1} onClick={() => { const a = [...pages]; [a[i + 1], a[i]] = [a[i], a[i + 1]]; setPages(a); }} className="rounded-full h-8 w-8"><ArrowDown className="w-3.5 h-3.5" /></Button>
                              <Button size="icon" variant="ghost" disabled={pages.length <= 1} onClick={() => setPages(pages.filter((_, j) => j !== i))} className="rounded-full h-8 w-8 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </div>

                          <div className="mt-3">
                            <Label className="text-xs text-slate-500">{lang === "fr" ? "Sections visibles sur cette page" : "Visible sections on this page"}</Label>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {AVAILABLE_SECTIONS.map((s) => {
                                const on = (p.sections || []).includes(s.key);
                                return (
                                  <button
                                    key={s.key}
                                    data-testid={`page-${i}-sec-${s.key}`}
                                    onClick={() => {
                                      const cur = p.sections || [];
                                      const next = on ? cur.filter((k) => k !== s.key) : [...cur, s.key];
                                      setPages(pages.map((x, j) => j === i ? { ...x, sections: next } : x));
                                    }}
                                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${on ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                  >
                                    {s.fr}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Per-page hero override — leave blank to reuse the home page's hero. */}
                          {(p.sections || []).includes("hero") && (
                            <details className="mt-4 group">
                              <summary className="cursor-pointer text-xs text-[#1E3A8A] font-medium">
                                {lang === "fr" ? "▸ Titre et sous-titre de cette page (optionnel)" : "▸ This page's title and subtitle (optional)"}
                              </summary>
                              <div className="mt-3 grid gap-2 pl-3 border-l-2 border-slate-100">
                                <Input
                                  data-testid={`page-${i}-hero-kicker`}
                                  value={p.hero_kicker || ""}
                                  onChange={(e) => setPages(pages.map((x, j) => j === i ? { ...x, hero_kicker: e.target.value } : x))}
                                  placeholder={lang === "fr" ? "Petit texte au-dessus (ex : « Notre équipe »)" : "Kicker text (e.g. « Our team »)"}
                                  className="text-sm"
                                />
                                <Input
                                  data-testid={`page-${i}-hero-title`}
                                  value={p.hero_title || ""}
                                  onChange={(e) => setPages(pages.map((x, j) => j === i ? { ...x, hero_title: e.target.value } : x))}
                                  placeholder={lang === "fr" ? "Grand titre de cette page" : "Big title for this page"}
                                  className="text-sm"
                                />
                                <Textarea
                                  data-testid={`page-${i}-hero-sub`}
                                  value={p.hero_sub || ""}
                                  onChange={(e) => setPages(pages.map((x, j) => j === i ? { ...x, hero_sub: e.target.value } : x))}
                                  placeholder={lang === "fr" ? "Sous-titre de cette page" : "Subtitle for this page"}
                                  rows={2}
                                  className="text-sm"
                                />
                              </div>
                            </details>
                          )}

                          {/* Per-page freeform text (rendered when "custom_text" section is enabled). */}
                          {(p.sections || []).includes("custom_text") && (
                            <div className="mt-4 grid gap-2 pl-3 border-l-2 border-[#F97316]/40">
                              <Label className="text-xs text-[#F97316] font-semibold">{lang === "fr" ? "Bloc texte libre" : "Custom text block"}</Label>
                              <Input
                                data-testid={`page-${i}-custom-title`}
                                value={p.custom_text_title || ""}
                                onChange={(e) => setPages(pages.map((x, j) => j === i ? { ...x, custom_text_title: e.target.value } : x))}
                                placeholder={lang === "fr" ? "Titre (optionnel)" : "Title (optional)"}
                                className="text-sm"
                              />
                              <Textarea
                                data-testid={`page-${i}-custom-text`}
                                value={p.custom_text || ""}
                                onChange={(e) => setPages(pages.map((x, j) => j === i ? { ...x, custom_text: e.target.value } : x))}
                                placeholder={lang === "fr" ? "Écrivez ici n'importe quel paragraphe — historique de l'entreprise, promesses, réglementation…" : "Write any paragraph — company history, promises, disclaimers…"}
                                rows={5}
                              />
                            </div>
                          )}
                        </div>
                      ))}

                      <Button
                        data-testid="page-add-btn"
                        variant="outline"
                        onClick={() => setPages([...pages, { slug: slugify(`page-${pages.length + 1}`), title: `Page ${pages.length + 1}`, sections: ["hero", "contact"] }])}
                        className="rounded-full w-fit"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> {lang === "fr" ? "Ajouter une page" : "Add a page"}
                      </Button>

                      {publishedSlug && (
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
                          <p className="font-semibold mb-1">{lang === "fr" ? "URLs de vos pages :" : "Your page URLs:"}</p>
                          <ul className="space-y-0.5 font-mono">
                            {pages.map((p, i) => (
                              <li key={i}>{window.location.origin}/site/{publishedSlug}{i === 0 ? "" : `/${p.slug}`}</li>
                            ))}
                          </ul>
                          <p className="mt-2 text-emerald-700 not-italic">{lang === "fr" ? "N'oubliez pas de cliquer sur « Publier / Mettre à jour » ci-dessous." : "Don't forget to click « Publish / Update » below."}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {tab === "domain" && (
                <div data-testid="tab-domain">
                  <Label>{lang === "fr" ? "Votre domaine personnalisé" : "Your custom domain"}</Label>
                  <Input
                    data-testid="site-domain"
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value.trim().toLowerCase())}
                    placeholder={lang === "fr" ? "ex : mabakery.ca (sans http://)" : "e.g. mybakery.com (no http://)"}
                    className="mt-1.5 max-w-md font-mono"
                    disabled={!publishedSlug}
                  />
                  <p className="text-xs text-slate-500 mt-2 max-w-md leading-relaxed">
                    {lang === "fr"
                      ? "Format attendu : uniquement le nom de domaine que vous avez acheté chez un registrar (GoDaddy, OVH, Namecheap…). Exemples valides : mabakery.ca · restaurant-2soeurs.com · le-2soeurs.fr. Pas d'espaces, pas d'accents, avec au moins un point."
                      : "Expected format: only the domain name you bought from a registrar (GoDaddy, Namecheap…). Valid examples: mybakery.com · restaurant-2sisters.com · le-2sisters.fr. No spaces, no accents, must contain a dot."}
                  </p>
                  {!publishedSlug && (
                    <p className="text-xs text-amber-700 mt-2">
                      {lang === "fr" ? "Publiez d'abord votre site pour pouvoir y connecter un domaine." : "Publish your site first to connect a domain."}
                    </p>
                  )}
                  {customDomain && publishedSlug && (
                    <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
                      <p className="font-semibold text-slate-900 mb-2">
                        {lang === "fr" ? "Configuration DNS chez votre registrar" : "DNS configuration at your registrar"}
                      </p>
                      <ol className="text-xs text-slate-600 space-y-1 list-decimal ml-4">
                        <li>{lang === "fr" ? "Ajoutez un enregistrement CNAME :" : "Add a CNAME record:"}</li>
                        <li className="font-mono bg-white border border-slate-200 rounded p-2 my-1">
                          {customDomain} &nbsp; CNAME &nbsp; sites.myproxijob.com
                        </li>
                        <li>{lang === "fr" ? "La propagation DNS prend 1-24h. Nous générons automatiquement un certificat SSL." : "DNS propagation takes 1-24h. We auto-generate an SSL certificate."}</li>
                      </ol>
                    </div>
                  )}
                  <Button data-testid="save-domain-btn" onClick={saveDomain} disabled={busy || !publishedSlug} className="mt-4 bg-[#1E3A8A] hover:bg-[#172554] rounded-full">
                    <Save className="w-4 h-4 mr-1.5" /> {lang === "fr" ? "Enregistrer le domaine" : "Save domain"}
                  </Button>
                </div>
              )}

              {tab === "stats" && (
                <div data-testid="tab-stats">
                  {!publishedSlug || !analytics ? (
                    <p className="text-sm text-slate-500">{lang === "fr" ? "Publiez votre site pour commencer à voir les statistiques." : "Publish your site to start seeing analytics."}</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatCard label={lang === "fr" ? "Vues 7j" : "Views 7d"} value={analytics.last_7?.view || 0} accent="#1E3A8A" />
                        <StatCard label={lang === "fr" ? "Vues 30j" : "Views 30d"} value={analytics.last_30?.view || 0} accent="#1E3A8A" />
                        <StatCard label={lang === "fr" ? "Contacts 7j" : "Contacts 7d"} value={(analytics.last_7?.contact_click || 0) + (analytics.last_7?.phone_click || 0)} accent="#F97316" />
                        <StatCard label={lang === "fr" ? "Clics site 7j" : "Site clicks 7d"} value={analytics.last_7?.website_click || 0} accent="#059669" />
                      </div>
                      {analytics.series_14d?.length > 0 && (
                        <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4">
                          <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">{lang === "fr" ? "Vues quotidiennes (14 derniers jours)" : "Daily views (last 14 days)"}</p>
                          <Sparkline series={analytics.series_14d} accent={content.accent_color || "#F97316"} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-slate-100 flex gap-3 flex-wrap items-center">
                <Button data-testid="site-publish-btn" onClick={publish} disabled={busy} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full h-11 px-6">
                  <Save className="w-4 h-4 mr-1.5" /> {lang === "fr" ? "Publier / Mettre à jour" : "Publish / Update"}
                </Button>
                {publicUrl && (
                  <a href={publicUrl} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="rounded-full h-11 px-5">
                      <ExternalLink className="w-4 h-4 mr-1.5" /> {lang === "fr" ? "Ouvrir le site" : "Open site"}
                    </Button>
                  </a>
                )}
                {publishedSlug && (
                  <Button
                    data-testid="site-delete-btn"
                    variant="ghost"
                    onClick={async () => {
                      if (!window.confirm(lang === "fr" ? "Dépublier et supprimer ce site IA ?" : "Unpublish and delete this AI site?")) return;
                      try {
                        await http.delete(`/websites/${cid}`);
                        toast.success(lang === "fr" ? "Site supprimé" : "Site deleted");
                        setContent(null);
                        setPublishedSlug(null);
                      } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
                    }}
                    className="ml-auto rounded-full text-red-600 hover:bg-red-50 h-11 px-4"
                  >
                    <Trash className="w-4 h-4 mr-1.5" /> {lang === "fr" ? "Supprimer le site" : "Delete site"}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        <p className="mt-6 text-xs text-slate-500 text-center">
          {lang === "fr" ? "Site hébergé sur myproxijob.com/site/<slug> ou sur votre domaine personnalisé." : "Site hosted on myproxijob.com/site/<slug> or your custom domain."}
        </p>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="font-display text-3xl font-bold mt-1" style={{ color: accent }}>{value.toLocaleString()}</p>
    </div>
  );
}

function Sparkline({ series, accent }) {
  const w = 640, h = 80, pad = 4;
  const max = Math.max(1, ...series.map((s) => s.views));
  const step = (w - pad * 2) / Math.max(1, series.length - 1);
  const points = series.map((s, i) => `${pad + i * step},${h - pad - (s.views / max) * (h - pad * 2)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
      <polyline fill="none" stroke={accent} strokeWidth="2" points={points} />
      {series.map((s, i) => (
        <circle key={i} cx={pad + i * step} cy={h - pad - (s.views / max) * (h - pad * 2)} r="2.5" fill={accent}>
          <title>{s.day}: {s.views}</title>
        </circle>
      ))}
    </svg>
  );
}
